import { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { LimiteDeErro } from '../componentes/LimiteDeErro';
import { PainelCliente } from '../componentes/painel/PainelCliente';
import { PainelGeral } from '../componentes/painel/PainelGeral';
import { PainelParticipante } from '../componentes/painel/PainelParticipante';
import {
  Aviso,
  Botao,
  CampoDeSelecao,
  EstadoCarregando,
  EstadoErro,
  EstadoVazio,
} from '../componentes/ui';
import { usePerfilOperacional } from '../hooks/usePainel';
import {
  useAutenticado,
  useIdentidade,
  usePrecisaEscolherEmpresa,
  useSemVinculo,
} from '../hooks/useSessao';
import { mensagemDoErro } from '../servicos/erros';
import { useExercicioVigente } from '../servicos/exercicio';
import type { DashboardIcms } from '../servicos/perfil';

/**
 * Painel da empresa corrente.
 *
 * ## Como a tela decide o que mostrar
 *
 * São três dashboards — cliente, assessor e fornecedor — e quem escolhe entre
 * eles é o papel de **negócio** da empresa, que não está no token e não deveria
 * estar (dívida #41). Quem responde é `GET /api/icms/perfil/:businessId`:
 *
 * 1. `dashboardPadrao === null` → empresa sem papel ICMS. **Não é erro nem tela
 *    vazia por falha**: é cadastro ativo que ainda não entrou em operação, e a
 *    tela diz isso com essas palavras.
 * 2. Um dashboard → abre direto.
 * 3. Mais de um (a mesma empresa pode ser cliente numa operação e fornecedora em
 *    outra, §2.1) → alternador, abrindo no `dashboardPadrao`. A ordem vem do
 *    backend e é estável: se ela mudasse a cada sessão, o painel que abre mudaria
 *    sem o usuário ter mexido em nada.
 *
 * Não há mais amostra de demonstração aqui. Enquanto o perfil não existia, a
 * tela rodava com uma — anunciada em letra grande, com selo em cada cartão e
 * valores redondos de propósito. O legado fazia o oposto: três widgets do
 * template Fuse com números fixos, sem nenhuma chamada de API e sem nada que os
 * distinguisse de dado real (dívida #49, RN-634). A regra que sobrevive à
 * remoção da amostra é a mesma: **o que não existe vira estado vazio ou
 * travessão, nunca um número.**
 */
export function Painel(): JSX.Element {
  const autenticado = useAutenticado();
  const precisaEscolher = usePrecisaEscolherEmpresa();
  const semEmpresa = useSemVinculo();

  if (!autenticado) return <Navigate to="/entrar" replace />;
  // RN-603 — sem empresa escolhida não se mostra número nenhum. A tela repete a
  // checagem da `RotaProtegida` porque ela vale mesmo se o painel for montado
  // fora dela um dia: guarda de tela não deve depender de como foi roteada.
  if (precisaEscolher || semEmpresa) return <Navigate to="/selecionar-empresa" replace />;

  return (
    <div className="min-h-screen bg-superficie-base">
      <main className="mx-auto w-full max-w-6xl px-4 py-8 sm:px-6">
        <LimiteDeErro area="o painel">
          <ConteudoDoPainel />
        </LimiteDeErro>
      </main>
    </div>
  );
}

/** Rótulo de cada dashboard — o papel da **empresa**, não o do usuário. */
const ROTULO_DO_DASHBOARD: Readonly<Record<DashboardIcms, string>> = {
  CLIENTE: 'Como cliente',
  ASSESSOR: 'Como assessor',
  FORNECEDOR: 'Como fornecedor',
};

/**
 * Anos oferecidos no seletor, do ano-alvo para trás.
 *
 * O primeiro é `atual + 1` de propósito: é o **ano-alvo**, aquele em que a
 * operação ainda está sendo montada e o dashboard do assessor devolve projeção
 * (RN-169). Sem ele na lista, a projeção que o backend calcula não teria como
 * ser vista.
 */
const ANOS_OFERECIDOS = 4;

/**
 * Os painéis que o admin alterna: a visão geral da operação (D-015) ou os da
 * empresa corrente. O Recorrente não tem aba própria: o detalhe dele está na
 * visão geral, e o filtro de serviço faz o papel das abas.
 */
type Visao = 'GERAL' | DashboardIcms;

function ConteudoDoPainel(): JSX.Element {
  const identidade = useIdentidade();
  const admin = identidade?.isAdmin === true;
  const perfil = usePerfilOperacional();
  // RN-620 — o exercício vigente, não o ano do calendário.
  const vigente = useExercicioVigente();
  const [ano, definirAno] = useState(() => vigente);
  // `null` = seguir o padrão do backend. Guardar a escolha em vez de inicializar
  // o estado com ela evita o efeito colateral clássico: um `useState(padrao)`
  // congelaria o valor da primeira renderização, quando o perfil ainda não
  // chegou.
  const [dashboardEscolhido, escolherDashboard] = useState<DashboardIcms | null>(null);
  /*
    O admin abre na visão geral: é a pergunta dele ("como está a operação, nos
    três serviços"), e não "como está a empresa em que estou logado". Os
    painéis da empresa continuam a um clique.
  */
  const [visao, definirVisao] = useState<Visao | null>(null);

  if (perfil.isPending) {
    return <EstadoCarregando variante="painel" contexto="o painel da empresa" />;
  }

  if (perfil.isError) {
    return (
      <EstadoErro
        titulo="Não foi possível carregar o painel"
        // Mensagem do servidor, nunca texto fixo (dívida #50, RN-627/RN-628).
        // `detalhe` fica de fora: é onde o legado vazava nome de tabela e coluna.
        mensagem={mensagemDoErro(perfil.error)}
        aoTentarNovamente={() => void perfil.refetch()}
      />
    );
  }

  const { dashboards, dashboardPadrao } = perfil.data;

  const visaoAtual: Visao | null = visao ?? (admin ? 'GERAL' : dashboardPadrao);

  if (visaoAtual === 'GERAL') {
    return (
      <div className="flex flex-col gap-8">
        {/* Só com o que alternar: sem papel na empresa corrente, não há botão. */}
        {dashboards.length > 0 && (
          <SeletorDeVisao atual="GERAL" visoes={['GERAL', ...dashboards]} aoTrocar={definirVisao} />
        )}
        <PainelGeral />
      </div>
    );
  }

  if (dashboardPadrao === null) {
    return <SemOperacao aoAtualizar={() => void perfil.refetch()} />;
  }

  // A escolha só vale se ainda fizer sentido para esta empresa: trocar de
  // vínculo pode trocar o conjunto de dashboards, e uma aba que não existe mais
  // renderizaria um painel que o servidor negaria.
  const atual =
    dashboardEscolhido !== null && dashboards.includes(dashboardEscolhido)
      ? dashboardEscolhido
      : dashboardPadrao;
  const seletorDeAno = <SeletorDeAno ano={ano} aoTrocar={definirAno} />;

  return (
    <div className="flex flex-col gap-8">
      {/* Só com o que alternar: uma linha vazia no topo seria espaço à toa. */}
      {admin ? (
        <SeletorDeVisao
          atual={atual}
          visoes={['GERAL', ...dashboards]}
          aoTrocar={(v) => {
            definirVisao(v);
            if (v !== 'GERAL') escolherDashboard(v);
          }}
        />
      ) : (
        dashboards.length > 1 && (
          <SeletorDeDashboard atual={atual} dashboards={dashboards} aoTrocar={escolherDashboard} />
        )
      )}

      {dashboards.length > 1 && (
        <Aviso tom="informacao" titulo="Esta empresa tem mais de um papel na operação">
          Os números de cada papel são apurados separadamente e não se somam: como cliente ela
          compra crédito, como assessor ou fornecedor ela recebe por intermediar ou transferir.
        </Aviso>
      )}

      {/*
        A `key` faz de cada combinação papel × ano uma montagem nova. A consulta
        já é trocada pela `queryKey`; o que a `key` garante é que nenhum estado
        interno do painel anterior (rolagem, ordenação futura) atravesse a troca
        e descreva um recorte que não é mais o exibido.
      */}
      {/* O ano fica na linha do "Resumo de …", dentro de cada painel. */}
      {atual === 'CLIENTE' ? (
        <PainelCliente key={`CLIENTE-${ano}`} ano={ano} seletorDeAno={seletorDeAno} />
      ) : (
        <PainelParticipante
          key={`${atual}-${ano}`}
          dashboard={atual}
          ano={ano}
          seletorDeAno={seletorDeAno}
        />
      )}
    </div>
  );
}

/**
 * Empresa sem papel de ICMS.
 *
 * Não é erro e não é falha de carregamento — os dois já foram tratados acima. É
 * uma empresa cadastrada que ainda não entrou em operação, e a microcopy diz
 * isso sem culpar o usuário nem sugerir que algo quebrou.
 */
function SemOperacao(props: { readonly aoAtualizar: () => void }): JSX.Element {
  return (
    <EstadoVazio
      titulo="Esta empresa ainda não está em operação de ICMS"
      descricao="O cadastro está ativo, mas ela ainda não foi vinculada como cliente, assessor ou fornecedor. Assim que a MS3 Capital registrar esse vínculo, o painel correspondente aparece aqui — nada precisa ser feito nesta tela."
      acao={
        <Botao variante="secundario" onClick={props.aoAtualizar}>
          Verificar novamente
        </Botao>
      }
    />
  );
}

/**
 * Alternador do admin: a operação inteira ou um dos painéis da empresa.
 *
 * Mesma mecânica do alternador de papel — `role="group"` com `aria-pressed`,
 * não abas —, com "Visão geral" na frente porque é onde o admin começa.
 */
function SeletorDeVisao(props: {
  readonly atual: Visao;
  readonly visoes: readonly Visao[];
  readonly aoTrocar: (visao: Visao) => void;
}): JSX.Element {
  return (
    <div role="group" aria-label="O que o painel mostra" className="flex flex-wrap gap-2">
      {props.visoes.map((visao) => {
        const ativo = visao === props.atual;
        return (
          <Botao
            key={visao}
            variante={ativo ? 'primario' : 'secundario'}
            tamanho="pequeno"
            aria-pressed={ativo}
            onClick={() => props.aoTrocar(visao)}
          >
            {visao === 'GERAL' ? 'Visão geral' : ROTULO_DO_DASHBOARD[visao]}
          </Botao>
        );
      })}
    </div>
  );
}

/**
 * Alternador de papel.
 *
 * `role="group"` com `aria-pressed`, e não um widget de abas: abas exigem
 * `tabpanel` associado e navegação por seta, e a biblioteca de UI não tem abas
 * de propósito. Anunciar "aba" ao leitor de tela sem entregar o comportamento é
 * o que a versão anterior da calculadora fazia — aqui não se inventa uma.
 */
function SeletorDeDashboard(props: {
  readonly atual: DashboardIcms;
  readonly dashboards: readonly DashboardIcms[];
  readonly aoTrocar: (dashboard: DashboardIcms) => void;
}): JSX.Element {
  return (
    <div role="group" aria-label="Papel da empresa na operação" className="flex flex-wrap gap-2">
      {props.dashboards.map((dashboard) => {
        const ativo = dashboard === props.atual;
        return (
          <Botao
            key={dashboard}
            variante={ativo ? 'primario' : 'secundario'}
            tamanho="pequeno"
            aria-pressed={ativo}
            onClick={() => props.aoTrocar(dashboard)}
          >
            {ROTULO_DO_DASHBOARD[dashboard]}
          </Botao>
        );
      })}
    </div>
  );
}

/**
 * Ano da operação.
 *
 * O ano fiscal é sempre o anterior (RN-180) e quem faz essa conta é o backend —
 * a tela só escolhe o ano da operação e o repassa. Ele entra na `queryKey` junto
 * com a empresa, então trocar de ano não reusa o cache do anterior.
 */
export function SeletorDeAno(props: {
  readonly ano: number;
  readonly aoTrocar: (ano: number) => void;
  /** Largura: estreito ao lado de um título; a coluna inteira numa grade de filtros. */
  readonly className?: string;
}): JSX.Element {
  const atual = useExercicioVigente();
  const opcoes = Array.from({ length: ANOS_OFERECIDOS }, (_, indice) => {
    const ano = atual + 1 - indice;
    return { valor: String(ano), rotulo: String(ano) };
  });

  return (
    <CampoDeSelecao
      rotulo="Ano da operação"
      className={props.className ?? 'w-40'}
      opcoes={opcoes}
      value={String(props.ano)}
      onChange={(evento) => props.aoTrocar(Number(evento.target.value))}
    />
  );
}
